/** @jsx jsx */
import { jsx } from 'hono/jsx/dom';
import { render } from 'hono/jsx/dom';
import { sendVerification, checkVerification, loginPassword, isSuccessResponse } from './apiClient';
import { tr } from '../lib/i18n';
import { safeRedirectPath } from '../lib/safeRedirect';

// Sign-in flow: the member enters their phone number; the server answers
// either "password required" (account has a password → show the password
// box) or "code sent" (no password yet, or forgot-password → show the code
// box). Anyone signing in by code without a password is sent to /set-password.

class LoginManager {
  private messageDiv: HTMLElement;
  private loginForm: HTMLFormElement;
  private verificationForm: HTMLElement;
  private passwordForm: HTMLElement;
  private verificationCodeInput: HTMLInputElement;
  private passwordInput: HTMLInputElement;
  // Set when the member asked for a code because they forgot their password;
  // a successful code then leads to /set-password to choose a new one.
  private forgotPassword = false;

  constructor() {
    this.messageDiv = document.getElementById('message') as HTMLElement;
    this.loginForm = document.getElementById('loginForm') as HTMLFormElement;
    this.verificationForm = document.getElementById('verificationForm') as HTMLElement;
    this.passwordForm = document.getElementById('passwordForm') as HTMLElement;
    this.verificationCodeInput = document.getElementById('verificationCode') as HTMLInputElement;
    this.passwordInput = document.getElementById('password') as HTMLInputElement;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.loginForm.addEventListener('submit', (e) => this.handleContinue(e, false));

    const verifyForm = document.getElementById('verifyForm') as HTMLFormElement;
    verifyForm.addEventListener('submit', this.handleVerifyCode.bind(this));

    const passwordLoginForm = document.getElementById('passwordLoginForm') as HTMLFormElement;
    passwordLoginForm.addEventListener('submit', this.handlePasswordLogin.bind(this));

    const forgot = document.getElementById('forgotPassword') as HTMLAnchorElement;
    forgot.addEventListener('click', (e) => this.handleContinue(e, true));
  }

  private phoneNumber(): string {
    return (window as any).signInTel.getNumber();
  }

  private async handleContinue(e: Event, forceSms: boolean): Promise<void> {
    e.preventDefault();

    try {
      const result = await sendVerification({ phoneNumber: this.phoneNumber(), mode: 'login', forceSms });

      if (isSuccessResponse(result)) {
        this.forgotPassword = forceSms;
        if (result.passwordRequired) {
          this.verificationForm.style.display = 'none';
          this.passwordForm.style.display = 'block';
          this.showMessage('', 'success');
          this.passwordInput.focus();
        } else {
          this.passwordForm.style.display = 'none';
          this.verificationForm.style.display = 'block';
          this.showMessage(tr('Verification code sent successfully!', '¡Código de verificación enviado!'), 'success');
          this.verificationCodeInput.focus();
        }
      } else {
        this.showMessage('Error: ' + result.error, 'error');
      }
    } catch (error) {
      this.showMessage(error instanceof Error ? error.message : tr('Network error. Please try again.', 'Error de red. Inténtalo de nuevo.'), 'error');
    }
  }

  // Return the user to the page they were trying to reach, if any. Only
  // honor same-origin paths to avoid an open-redirect.
  private destination(): string {
    return safeRedirectPath(new URLSearchParams(window.location.search).get('redirect'));
  }

  private async handlePasswordLogin(e: Event): Promise<void> {
    e.preventDefault();

    try {
      const result = await loginPassword({ phoneNumber: this.phoneNumber(), password: this.passwordInput.value });

      if (isSuccessResponse(result)) {
        this.showMessage(tr('Signed in! Redirecting...', '¡Sesión iniciada! Redirigiendo...'), 'success');
        window.location.href = this.destination();
      } else {
        this.showMessage('Error: ' + result.error, 'error');
      }
    } catch (error) {
      this.showMessage(error instanceof Error ? error.message : tr('Network error. Please try again.', 'Error de red. Inténtalo de nuevo.'), 'error');
    }
  }

  private async handleVerifyCode(e: Event): Promise<void> {
    e.preventDefault();

    try {
      const result = await checkVerification({ phoneNumber: this.phoneNumber(), code: this.verificationCodeInput.value });

      if (isSuccessResponse(result)) {
        this.showMessage(tr('Verification successful! Redirecting...', '¡Verificación exitosa! Redirigiendo...'), 'success');
        const destination = this.destination();
        // No password yet (or they forgot it): set one before going on.
        const next = result.needsPassword || this.forgotPassword
          ? '/set-password?redirect=' + encodeURIComponent(destination)
          : destination;
        setTimeout(() => {
          window.location.href = next;
        }, 1000);
      } else {
        this.showMessage('Error: ' + result.error, 'error');
      }
    } catch (error) {
      this.showMessage(error instanceof Error ? error.message : tr('Network error. Please try again.', 'Error de red. Inténtalo de nuevo.'), 'error');
    }
  }

  private showMessage(message: string, type: 'success' | 'error'): void {
    this.messageDiv.innerHTML = '';
    if (!message) return;
    const color = type === 'success' ? 'green' : 'red';
    render(<p style={`color: ${color};`}>{message}</p>, this.messageDiv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new LoginManager();
});
